import { Link } from "@tanstack/react-router";
import { Menu, X } from "lucide-react";
import { AnimatePresence, motion, useReducedMotion } from "motion/react";
import { useCallback, useEffect, useRef, useState } from "react";

import { heroFor } from "@/data/heroes";
import { primaryNav, site } from "@/data/site";
import { cn } from "@/lib/utils";

/**
 * Flattened menu entries: top-level links plus their children.
 * A parent with children is a plain label (no link, no hover swap) — its
 * children carry the destinations.
 */
const menuEntries = primaryNav.flatMap((item) => [
  { label: item.label, to: item.to, child: false, group: Boolean(item.children?.length) },
  ...(item.children ?? []).map((child) => ({
    label: child.label,
    to: child.to,
    child: true,
    group: false,
  })),
]);

export function Header({ overlay = true }: { overlay?: boolean }) {
  const [open, setOpen] = useState(false);
  const [warm, setWarm] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const reduceMotion = useReducedMotion();
  const panelRef = useRef<HTMLDivElement | null>(null);
  const closeRef = useRef<HTMLButtonElement | null>(null);
  const triggerRef = useRef<HTMLElement | null>(null);

  const openMenu = useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
    triggerRef.current = event.currentTarget;
    setWarm(true);
    setOpen(true);
  }, []);

  /** Start fetching the menu photos on intent (hover/focus of the trigger). */
  const warmMenu = useCallback(() => setWarm(true), []);

  const close = useCallback(() => setOpen(false), []);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  useEffect(() => {
    if (open) {
      // Move focus into the dialog so keyboard users land inside it
      const id = window.setTimeout(() => closeRef.current?.focus(), 60);
      return () => window.clearTimeout(id);
    }
    triggerRef.current?.focus();
    return undefined;
  }, [open]);

  useEffect(() => {
    if (!open) return;
    setActiveIndex(0);
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") close();
      if (event.key !== "Tab") return;
      const focusable = panelRef.current?.querySelectorAll<HTMLElement>(
        "a[href], button:not([disabled])",
      );
      if (!focusable || focusable.length === 0) return;
      const first = focusable[0]!;
      const last = focusable[focusable.length - 1]!;
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, close]);

  const activeImage = heroFor(menuEntries[activeIndex]?.to ?? "/").image;

  return (
    <header
      className={cn("z-50 w-full", overlay ? "absolute inset-x-0 top-0" : "relative bg-navy")}
    >
      <a href="#main" className="skip-link">
        Skip to content
      </a>
      <div className="container-site grid grid-cols-[minmax(0,1fr)_auto] items-center gap-4 py-4 sm:flex sm:justify-between sm:gap-6 sm:py-5">
        <Link to="/" aria-label={`${site.name} home`} className="min-w-0 shrink-0">
          <img
            src={site.logo}
            alt={site.name}
            className="h-12 w-auto origin-center transition-transform duration-300 ease-out hover:scale-[1.3]"
          />
        </Link>

        <nav className="flex items-center gap-3 sm:gap-5">
          <button
            type="button"
            onClick={openMenu}
            onMouseEnter={warmMenu}
            onFocus={warmMenu}
            aria-expanded={open}
            className="hidden min-h-11 items-center gap-2 rounded px-2 font-heading text-sm uppercase tracking-widest text-white transition-opacity hover:opacity-70 sm:flex"
          >
            <Menu className="h-4 w-4" aria-hidden />
            Menu
          </button>
          <span className="hidden h-5 w-px bg-white/40 sm:block" aria-hidden />
          <Link
            to="/crusades"
            className="hidden min-h-11 items-center font-heading text-sm uppercase tracking-widest text-white transition-opacity hover:opacity-70 sm:inline-flex"
          >
            Crusades
          </Link>
          <div className="hidden overflow-hidden rounded border-[3px] border-brand sm:flex">
            <Link
              to="/donate"
              className="flex min-h-11 items-center bg-white px-4 py-2 text-sm font-bold text-ink transition-colors hover:bg-brand hover:text-brand-foreground"
            >
              Donate
            </Link>
            <Link
              to="/join-us"
              className="flex min-h-11 items-center bg-brand px-4 py-2 text-sm font-bold text-brand-foreground transition-opacity hover:opacity-90"
            >
              Join Us
            </Link>
          </div>
          <button
            type="button"
            onClick={openMenu}
            aria-label="Open menu"
            aria-expanded={open}
            className="-mr-2 flex min-h-11 min-w-11 items-center justify-center text-white sm:hidden"
          >
            <Menu className="h-7 w-7" aria-hidden />
          </button>
        </nav>
      </div>

      {/* Menu hero photos are warmed only once the visitor opens the menu, so
          ordinary page views never pay for images they may not see. */}
      {warm ? (
        <div className="hidden" aria-hidden>
          {menuEntries.map((entry) => (
            <img
              key={entry.to + entry.label}
              src={heroFor(entry.to).image}
              alt=""
              loading="lazy"
              decoding="async"
            />
          ))}
        </div>
      ) : null}

      <AnimatePresence>
        {open ? (
          <motion.div
            ref={panelRef}
            role="dialog"
            aria-modal="true"
            aria-label="Site menu"
            initial={reduceMotion ? { opacity: 1 } : { opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={reduceMotion ? { opacity: 1 } : { opacity: 0 }}
            transition={{ duration: 0.35, ease: "easeOut" }}
            className="fixed inset-0 z-[60] flex flex-col bg-navy sm:flex-row"
          >
            {/* Image panel — left */}
            <div className="relative hidden h-40 w-full shrink-0 overflow-hidden sm:block sm:h-full sm:w-1/2">
              <AnimatePresence initial={false}>
                <motion.img
                  key={activeImage}
                  src={activeImage}
                  alt=""
                  aria-hidden
                  initial={reduceMotion ? { opacity: 1 } : { opacity: 0, scale: 1.04 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.6, ease: "easeOut" }}
                  className="absolute inset-0 h-full w-full object-cover"
                />
              </AnimatePresence>
              <div className="absolute inset-0 bg-navy/25" aria-hidden />
              <img
                src="/loli-mark.png"
                alt=""
                aria-hidden
                className="absolute bottom-6 left-6 h-14 w-14 drop-shadow-lg sm:h-16 sm:w-16"
              />
            </div>

            {/* Text panel — right */}
            <div className="relative flex min-h-0 flex-1 flex-col overflow-y-auto">
              <div className="flex items-center justify-end px-6 py-5 sm:px-10">
                <button
                  ref={closeRef}
                  type="button"
                  onClick={close}
                  aria-label="Close menu"
                  className="-mr-2 flex min-h-11 min-w-11 items-center justify-center text-white transition-opacity hover:opacity-70"
                >
                  <X className="h-7 w-7" aria-hidden />
                </button>
              </div>

              <nav className="flex-1 px-6 pb-6 sm:px-10">
                <ul className="flex flex-col gap-1">
                  {menuEntries.map((entry, index) => (
                    <motion.li
                      key={entry.to + entry.label}
                      initial={reduceMotion ? { opacity: 1 } : { opacity: 0, y: 18 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{
                        duration: 0.4,
                        delay: reduceMotion ? 0 : 0.08 + index * 0.05,
                        ease: "easeOut",
                      }}
                    >
                      {entry.group ? (
                        <span className="block py-1 font-heading uppercase leading-tight text-white/60 text-3xl sm:text-4xl lg:text-5xl">
                          {entry.label}
                        </span>
                      ) : (
                        <Link
                          to={entry.to}
                          onClick={close}
                          onMouseEnter={() => setActiveIndex(index)}
                          onFocus={() => setActiveIndex(index)}
                          className={cn(
                            "block font-heading uppercase leading-tight transition-all duration-300",
                            entry.child
                              ? "py-1 pl-6 text-lg sm:text-xl"
                              : "py-1 text-3xl sm:text-4xl lg:text-5xl",
                            activeIndex === index ? "text-brand" : "text-white hover:text-brand",
                          )}
                        >
                          {entry.label}
                        </Link>
                      )}
                    </motion.li>
                  ))}
                </ul>
              </nav>

              <div className="border-t border-white/15 px-6 py-6 text-sm text-white/75 sm:px-10">
                <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
                  <a
                    href={site.phoneHref}
                    className="inline-flex min-h-11 items-center hover:text-brand"
                  >
                    {site.phone}
                  </a>
                  <a
                    href={`mailto:${site.email}`}
                    className="inline-flex min-h-11 items-center hover:text-brand"
                  >
                    {site.email}
                  </a>
                  <a
                    href={site.addressUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="hover:text-brand"
                  >
                    {site.address}
                  </a>
                </div>
                <div className="mt-4 flex flex-wrap gap-x-5 gap-y-2 font-heading uppercase tracking-widest">
                  {site.socials.map((social) => (
                    <a
                      key={social.label}
                      href={social.href}
                      target="_blank"
                      rel="noreferrer"
                      className="hover:text-brand"
                    >
                      {social.label}
                    </a>
                  ))}
                </div>
              </div>
            </div>
          </motion.div>
        ) : null}
      </AnimatePresence>
    </header>
  );
}
